// Type-safe localStorage utility with error handling and in-memory fallback

import { createLogger } from './logger';
import { MemoryStorage } from './memoryStorage';

export interface LocalStorageError {
  key: string;
  operation: 'get' | 'set' | 'remove' | 'clear';
  error: Error;
}

export interface LocalStorageOptions {
  fallbackValue?: any;
  serialize?: (value: any) => string;
  deserialize?: (value: string) => any;
  onError?: (error: LocalStorageError) => void;
}

class LocalStorageUtil {
  private logger = createLogger('LocalStorage', { flag: 'CACHE' });
  private isAvailable: boolean;
  private fallbackStorage: MemoryStorage | null = null;
  private usingFallback: boolean = false;

  constructor() {
    this.isAvailable = this.checkAvailability();

    if (!this.isAvailable) {
      this.fallbackStorage = new MemoryStorage();
      this.usingFallback = true;

      if (this.logger.isEnabled()) {
        this.logger.warn('localStorage unavailable - using in-memory fallback');
      }
    }
  }

  /**
   * Enhanced localStorage availability check
   * Tests both access and write capability to detect private mode
   */
  private checkAvailability(): boolean {
    try {
      // Check if localStorage exists
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }

      const testKey = '__localStorage_test__';

      // Try to write and read - this will fail in iOS Safari private mode
      localStorage.setItem(testKey, 'test');
      const retrieved = localStorage.getItem(testKey);
      localStorage.removeItem(testKey);

      // Verify the write actually worked
      return retrieved === 'test';
    } catch (e) {
      // Catch QuotaExceededError (private mode with quota=0)
      // Catch SecurityError (localStorage disabled by policy)
      // Catch any other DOMException
      if (this.logger.isEnabled() && e instanceof DOMException) {
        this.logger.warn('localStorage check failed', {
          errorName: e.name,
          errorMessage: e.message
        });
      }
      return false;
    }
  }

  /**
   * Get the storage instance (either localStorage or fallback)
   */
  private getStorage(): Storage {
    return this.isAvailable ? localStorage : (this.fallbackStorage as Storage);
  }

  private handleError(key: string, operation: LocalStorageError['operation'], error: Error, onError?: (error: LocalStorageError) => void): void {
    const localStorageError: LocalStorageError = { key, operation, error };
    
    if (onError) {
      onError(localStorageError);
    } else if (this.logger.isEnabled()) {
      this.logger.error('LocalStorage operation failed', {
        key,
        operation,
        message: error.message
      });
    }
  }

  private defaultSerialize(value: any): string {
    return JSON.stringify(value);
  }

  private defaultDeserialize(value: string): any {
    try {
      return JSON.parse(value);
    } catch {
      // If JSON parsing fails, return the raw string
      return value;
    }
  }

  /**
   * Get an item from localStorage (or fallback) with type safety
   */
  getItem<T = any>(key: string, options: LocalStorageOptions = {}): T | null {
    const { fallbackValue = null, deserialize = this.defaultDeserialize, onError } = options;

    try {
      const storage = this.getStorage();
      const item = storage.getItem(key);

      if (item === null) {
        return fallbackValue;
      }

      return deserialize(item) as T;
    } catch (error) {
      this.handleError(key, 'get', error as Error, onError);
      return fallbackValue;
    }
  }

  /**
   * Set an item in localStorage (or fallback) with type safety
   */
  setItem<T = any>(key: string, value: T, options: LocalStorageOptions = {}): boolean {
    const { serialize = this.defaultSerialize, onError } = options;

    try {
      const storage = this.getStorage();
      const serializedValue = serialize(value);
      storage.setItem(key, serializedValue);
      return true;
    } catch (error) {
      this.handleError(key, 'set', error as Error, onError);
      return false;
    }
  }

  /**
   * Remove an item from localStorage (or fallback)
   */
  removeItem(key: string, options: LocalStorageOptions = {}): boolean {
    const { onError } = options;

    try {
      const storage = this.getStorage();
      storage.removeItem(key);
      return true;
    } catch (error) {
      this.handleError(key, 'remove', error as Error, onError);
      return false;
    }
  }

  /**
   * Clear all items from localStorage (or fallback)
   */
  clear(options: LocalStorageOptions = {}): boolean {
    const { onError } = options;

    try {
      const storage = this.getStorage();
      storage.clear();
      return true;
    } catch (error) {
      this.handleError('*', 'clear', error as Error, onError);
      return false;
    }
  }

  /**
   * Check if a key exists in localStorage (or fallback)
   */
  hasItem(key: string): boolean {
    try {
      const storage = this.getStorage();
      return storage.getItem(key) !== null;
    } catch {
      return false;
    }
  }

  /**
   * Get all keys from localStorage (or fallback)
   */
  getAllKeys(): string[] {
    try {
      const storage = this.getStorage();
      const keys: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key) {
          keys.push(key);
        }
      }
      return keys;
    } catch {
      return [];
    }
  }

  /**
   * Get the size of storage in bytes (approximate)
   * Works with both localStorage and fallback storage
   */
  getSize(): number {
    try {
      // If using fallback storage, use its getSize method
      if (this.usingFallback && this.fallbackStorage) {
        return this.fallbackStorage.getSize();
      }

      // Otherwise calculate localStorage size
      const storage = this.getStorage();
      let total = 0;
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key) {
          const value = storage.getItem(key);
          if (value) {
            total += (value.length + key.length) * 2; // UTF-16 encoding
          }
        }
      }
      return total;
    } catch {
      return 0;
    }
  }

  /**
   * Check if localStorage is available
   */
  get available(): boolean {
    return this.isAvailable;
  }

  /**
   * Check if using fallback storage (in-memory)
   * This indicates localStorage is unavailable (private mode, disabled, etc.)
   */
  get isUsingFallback(): boolean {
    return this.usingFallback;
  }
}

// Create and export a singleton instance
export const localStorageUtil = new LocalStorageUtil();

// Export the class for testing purposes
export { LocalStorageUtil };

// Convenience functions for common operations
export const getStoredItem = <T = any>(key: string, fallback?: T): T | null => {
  return localStorageUtil.getItem<T>(key, { fallbackValue: fallback });
};

export const setStoredItem = <T = any>(key: string, value: T): boolean => {
  return localStorageUtil.setItem(key, value);
};

export const removeStoredItem = (key: string): boolean => {
  return localStorageUtil.removeItem(key);
};

export const clearStorage = (): boolean => {
  return localStorageUtil.clear();
};
